// src/components/MapPicker.js
import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './MapPicker.css'; // We'll create this next

// Custom marker icon with Bootstrap Icons
const createCustomIcon = () => {
  return L.divIcon({
    html: `<i class="bi bi-geo-alt-fill" style="font-size: 2rem; color: #dc3545;"></i>`,
    className: 'custom-marker-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

const MapPicker = ({ initialLocation, initialCoordinates, onSelect, className = '' }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    // Initialize map
    mapInstance.current = L.map(mapRef.current).setView(
      initialCoordinates || [-29.413435, -66.856770], 
      13
    );

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
      detectRetina: true
    }).addTo(mapInstance.current);

    // Add initial marker if coordinates exist
    if (initialCoordinates) {
      markerRef.current = L.marker(initialCoordinates, {
        icon: createCustomIcon()
      }).addTo(mapInstance.current);
    }

    // Click handler for adding/updating marker
    const handleMapClick = async (e) => {
      const { lat, lng } = e.latlng;
      
      // Remove previous marker if exists
      if (markerRef.current) {
        mapInstance.current.removeLayer(markerRef.current);
      }
      
      // Add new marker with custom icon
      markerRef.current = L.marker([lat, lng], {
        icon: createCustomIcon()
      }).addTo(mapInstance.current);
      
      try {
        // Reverse geocode to get location name
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
        );
        const data = await response.json();
        const locationName = data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        
        if (onSelect) {
          onSelect(locationName, [lat, lng]);
        }
      } catch (error) {
        const locationName = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        if (onSelect) {
          onSelect(locationName, [lat, lng]);
        }
      }
    };

    mapInstance.current.on('click', handleMapClick);

    // Cleanup function
    return () => {
      if (mapInstance.current) {
        mapInstance.current.off('click', handleMapClick);
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [initialCoordinates, onSelect]);

  return (
    <div 
      ref={mapRef} 
      className={`map-picker ${className}`}
    />
  );
};

export default MapPicker;